import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import MobileHome from '../../components/Home/Mobile/MobileHome'
import DefaultHome from '../../components/Home/Default/DefaultHome'
import ChatHistory from '@/ui/components/ChatHistory/ChatHistory'
import ChatHistorySkeleton from '@/ui/components/SkeletonLoading/ChatHistorySkeleton'
import { ActivityIndicator } from '@bosch/react-frok'

// import 

// utils
import { useSpace } from '@/hooks/useSpace'
import { useAuthentication } from '@/hooks/useAuthentication'
import { useNavigation } from '@/hooks/useNavigation'
import { Settings } from '@/constants'
import useWindowSize from '@/hooks/useWindowSize'
import Assistant from '@/utils/assistant/assistant'
import { ChatMessage } from '@/shared/types/chat'
import useGenericStore from '@/hooks/useGenericStore'

import './Home.scss'

const Home = () => {
    const { switchAssistant, getActiveSpace, loadSpaceHistory, isActivating } = useSpace()
    const { setGenericStoreValue } = useGenericStore()
    const { navigateTo } = useNavigation()
    const { information } = useAuthentication()
    const { t } = useTranslation()
    const { isMobile } = useWindowSize()

    const activeSpace = getActiveSpace()
    const [chatList, setChatList] = useState<ChatMessage[]>([])
    const [defaultSpaceLoaded, setDefaultSpaceLoaded] = useState(false)
    const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false)
    const isAuthorizationInProgress = sessionStorage.getItem('isAuthorizationInProgress') === 'true'
    const isAccountAvailable = !!information?.account?.name
    const isSpaceAvailable = !!activeSpace
    // The space is flagged active when its switch starts: it is ready when its history and metadata are there
    const isLoading = isAuthorizationInProgress || !isAccountAvailable || !isSpaceAvailable || isActivating

    useEffect(() => {
        setGenericStoreValue('showAssistantInfo', false, true)

        return () => {
            setGenericStoreValue('showAssistantInfo', true, true)
        }
        // the flag is set on mount and reset on unmount only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (activeSpace?.id && defaultSpaceLoaded) {
            const fetchChatHistory = async () => {
                setIsChatHistoryLoading(true)
                try {
                    const history = await loadSpaceHistory(activeSpace.id)
                    setChatList(history)
                } finally {
                    setIsChatHistoryLoading(false)
                }
            }
            fetchChatHistory();
        }
        // the history is loaded when the space changes or its default is loaded, not when the loader function changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSpace?.id, defaultSpaceLoaded]);

     useEffect(() => {
        const loadDefaultSpace = async () => {
            if (activeSpace && !defaultSpaceLoaded) {
                setDefaultSpaceLoaded(true)
            }
        }
        loadDefaultSpace()
        // the default space is flagged once per space
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSpace?.id])

    const onContactUs = () => {
        window.location.href = `mailto:${Settings.contact.mail}`
    }

    const onTileAction = async (assistant: Assistant) => {
        await switchAssistant(assistant.config.id)

        if (!activeSpace) return

        navigateTo(`${activeSpace.path}/${assistant.path}`)
    }

    if (isLoading || !activeSpace) {
        return (
            <div className='home-container home-loading'>
                <div className="loading">
                    <ActivityIndicator size="small" />
                </div>
            </div>
        )
    }

    return (
        <div className='home-container home-page'>
            {isMobile && (
                <MobileHome
                    space={activeSpace}
                    information={information}
                    translate={t}
                    tileHandler={onTileAction}
                    contactHandler={onContactUs}
                />
            )}

            {!isMobile && (
                <DefaultHome
                    space={activeSpace}
                    information={information}
                    translate={t}
                    tileHandler={onTileAction}
                    contactHandler={onContactUs}
                />
            )}

            {isChatHistoryLoading ? (
                <ChatHistorySkeleton isMobile={isMobile} itemCount={5} />
            ) : chatList?.length > 0 ? (
                <ChatHistory translate={t} chatMessages={chatList} isMobile={isMobile} />
            ) : null}
        </div>
    )
}

export default Home
